import { useSelector } from 'react-redux';
import { sequenceDetailsSelector } from '../../views/Sequences/actions/reducers';
import { getFormSelector } from '../../reducers/FormSelectorReducer';

/**
 * Checks if the provided value is not null, not an empty string, not an empty array, 'None', 'AND',
 * and not an object with all null or empty values. It recursively checks arrays and objects.
 * @param {any} value - The value to check for emptiness.
 * @returns {boolean} Returns `true` if the value is non-empty, otherwise `false`.
 */
export const isValueNotNullOrEmpty = (value) => {
  // Check for boolean true, non-empty string not equal to "None" or "AND", and non-null non-zero number
  if (
    value === true ||
    (typeof value === 'string' && value.trim() !== '' && value.toLowerCase() !== 'none' && value !== 'AND') ||
    (typeof value === 'number' && value !== 0)
  ) {
    return true;
  }
  // Check for non-empty array with at least one non-null element
  if (Array.isArray(value)) {
    return value.some(isValueNotNullOrEmpty);
  }
  // Check for non-null object with at least one non-null value
  if (typeof value === 'object' && value !== null) {
    return Object.values(value).some(isValueNotNullOrEmpty);
  }
  // Return false if none of the above conditions are met
  return false;
};

export const useUserSelectors = () => {
  const selectors = useSelector((state) => {
    const formSelector = state?.form?.sequenceDetails ? sequenceDetailsSelector : getFormSelector();
    if (!formSelector) {
      console.warn('Form selector not found in state');
      return {};
    }

    return {
      criteriaAnuraScore: formSelector(state, 'criteriaAnuraScore'),
      criteriaOfferRequiresOptIn: formSelector(state, 'criteriaOfferRequiresOptIn'),
      criteriaTcpaLastOptInInterval: formSelector(state, 'criteriaTcpaLastOptInInterval'),
      criteriaPhoneHasBeenVerifiedWithBriteverify: formSelector(state, 'criteriaPhoneHasBeenVerifiedWithBriteverify'),
      criteriaEmailHasBeenVerifiedWithBriteverify: formSelector(state, 'criteriaEmailHasBeenVerifiedWithBriteverify'),
      criteriaPhoneBlacklist: formSelector(state, 'criteriaPhoneBlacklist'),
      englishBrowserLanguageRequired: formSelector(state, 'englishBrowserLanguageRequired'),
      criteriaMinAge: formSelector(state, 'criteriaMinAge'),
      criteriaMaxAge: formSelector(state, 'criteriaMaxAge'),
      browserType: formSelector(state, 'criteriaExcludeBrowserType'),
      criteriaExcludeGender: formSelector(state, 'criteriaExcludeGender'),
      criteriaUserDataIncludeExclude: formSelector(state, 'criteriaUserDataIncludeExclude'),
      criteriaRequiredUserData: formSelector(state, 'criteriaRequiredUserData'),
      criteriaAibListIncludeExclude: formSelector(state, 'criteriaAibListIncludeExclude'),
      criteriaAibList: formSelector(state, 'criteriaAibList'),
      previousRegistrationConfiguration: formSelector(state, 'previousRegistrationConfiguration'),
      ipqsConfiguration: formSelector(state, 'ipqsConfiguration'),
      criteriaNullSourceNewUserIncludeExclude: formSelector(state, 'criteriaNullSourceNewUserIncludeExclude'),
      userIdLastDigitIncludeExclude: formSelector(state, 'criteriaIncludeOrExcludeUserIdLastDigit'),
      criteriaUserIdLastDigitString: formSelector(state, 'criteriaUserIdLastDigitString'),
      criteriaIncludeOrExcludeDevices: formSelector(state, 'criteriaIncludeOrExcludeDevices'),
      criteriaDevices: formSelector(state, 'criteriaDevices'),
      criteriaIncludeOrExcludeBrowsers: formSelector(state, 'criteriaIncludeOrExcludeBrowsers'),
      criteriaBrowsers: formSelector(state, 'criteriaBrowsers'),
      criteriaIncludeOrExcludeDomains: formSelector(state, 'criteriaIncludeOrExcludeDomains'),
      criteriaDomains: formSelector(state, 'criteriaDomains'),
      criteriaIncludeOrExcludeOperatingSystems: formSelector(state, 'criteriaIncludeOrExcludeOperatingSystems'),
      criteriaOperatingSystems: formSelector(state, 'criteriaOperatingSystems'),
      criteriaIncludeOrExcludeStates: formSelector(state, 'criteriaIncludeOrExcludeStates'),
      criteriaStates: formSelector(state, 'criteriaStates'),
      criteriaIncludeOrExcludeZips: formSelector(state, 'criteriaIncludeOrExcludeZips'),
      criteriaZips: formSelector(state, 'criteriaZips'),
      jobKeywordIncludeExclude: formSelector(
        state,
        'jobKeyword.includeExclude'
      ),
      jobKeywords: formSelector(state, 'jobKeyword.jobKeywords'),
    };
  });

  const isAnySelectorNotNullOrEmpty = Object.values(selectors).some(isValueNotNullOrEmpty);

  return {
    ...selectors,
    isAnySelectorNotNullOrEmpty,
  };
};

export const useAttributeSelectors = () => {
  const selectors = useSelector((state) => {
    const formSelector = state?.form?.sequenceDetails ? sequenceDetailsSelector : getFormSelector();
    if (!formSelector) {
      console.warn('Form selector not found in state');
      return {};
    }

    return {
      criteriaAffiliate: formSelector(state, 'criteriaIncludeOrExcludeAffNames'),
      criteriaEmail: formSelector(state, 'criteriaIncludeOrExcludeEmailDomains'),
      criteriaIncludeOrExcludeAffNames: formSelector(state, 'criteriaIncludeOrExcludeAffNames'),
      criteriaAffNames: formSelector(state, 'criteriaAffNames'),
      domainAffiliateConfiguration: formSelector(state, 'domainAffiliateConfiguration'),
      criteriaLandingPageGroupIncludeExclude: formSelector(state, 'criteriaLandingPageGroupIncludeExclude'),
      criteriaLandingPageGroupIds: formSelector(state, 'criteriaLandingPageGroupIds'),
      criteriaLandingPageIncludeExclude: formSelector(state, 'criteriaLandingPageIncludeExclude'),
      criteriaLandingPageIds: formSelector(state, 'criteriaLandingPageIds'),
      criteriaIncludeOrExcludeEmailDomains: formSelector(state, 'criteriaIncludeOrExcludeEmailDomains'),
      criteriaEmailDomains: formSelector(state, 'criteriaEmailDomains'),
      selectedQuestion: formSelector(state, 'criteriaQuestion'),
      criteriaQuestionAnswers: formSelector(state, 'criteriaQuestionAnswers'),
      userAttributeRules: formSelector(state, 'userAttributeRules'),
      userAttributeSelect: formSelector(state, 'criteriaIncludeOrExcludeUserAttributes'),
      urlParameterRules: formSelector(state, 'urlParameterRules'),
      urlParameterSelect: formSelector(state, 'criteriaIncludeOrExcludeUrlParameters'),
      userAgentRules: formSelector(state, 'userAgentRules'),
      userAgentSelect: formSelector(state, 'criteriaIncludeOrExcludeUserAgent'),
      affSubIDConfiguration: formSelector(state, 'affSubIDConfiguration'),
      qualityScoreCriteria: formSelector(state, 'qualityScoreCriteria'),
    };
  });

  const isAnySelectorNotNullOrEmpty = Object.values(selectors).some(isValueNotNullOrEmpty);

  return {
    ...selectors,
    isAnySelectorNotNullOrEmpty,
  };
};

export const usePrepingSelectors = () => {
  const selectors = useSelector((state) => {
    const formSelector = state?.form?.sequenceDetails ? sequenceDetailsSelector : getFormSelector();
    if (!formSelector) {
      console.warn('Form selector not found in state');
      return {};
    }

    return {
      suppressionRulesDurationType: formSelector(state, 'suppressionRules.duration.type'),
      suppressionRulesDurationAmount: formSelector(state, 'suppressionRules.duration.amount'),
      suppressionRulesSuppressionType: formSelector(state, 'suppressionRules.suppression_type'),
      suppressionRulesDurationMaxLimit: formSelector(state, 'suppressionRules.duration.max_limit'),
      suppressionRulesAdditionalOfferIds: formSelector(state, 'suppressionRules.additional_offer_ids'),
      suppressionRuleAnswer: formSelector(state, 'suppressionRules.suppression_answer'),
      suppressionRulesAdditionalOfferActionIds: formSelector(state, 'suppressionRules.additional_offer_action_ids'),
      prePingResultSessionCapture: formSelector(state, 'prePingResultSessionCapture'),
      externalPrePingProcessingEnabled: formSelector(state, 'externalPrePingProcessingEnabled'),
      prePingMethod: formSelector(state, 'prePingMethod'),
      prePingHandlerId: formSelector(state, 'prePingHandlerId'),
      prePing: formSelector(state, 'prePing'),
      prePingBody: formSelector(state, 'prePingBody'),
      prePingTimeout: formSelector(state, 'prePingTimeout'),
      prePingHeader1: formSelector(state, 'prePingHeader1'),
      prePingHeader2: formSelector(state, 'prePingHeader2'),
      prePingHeader3: formSelector(state, 'prePingHeader3'),
      prePingCustomHttpStatus: formSelector(state, 'prePingCustomHttpStatus'),
      andOr: formSelector(state, 'internalPrePingConfiguration.andOrLogic'),
      internalPrePings: formSelector(state, 'internalPrePingConfiguration.internalPrePings'),
    };
  });

  const isAnySelectorNotNullOrEmpty = Object.values(selectors).some(isValueNotNullOrEmpty);

  return {
    ...selectors,
    isAnySelectorNotNullOrEmpty,
  };
};

export const useDailyRestrictionSelectors = () => {
  const selectors = useSelector((state) => {
    const formSelector = state?.form?.sequenceDetails ? sequenceDetailsSelector : getFormSelector();
    if (!formSelector) {
      console.warn('Form selector not found in state');
      return {};
    }

    return {
      criteriaDailyLimit: formSelector(state, 'criteriaDailyLimit'),
      criteriaDailyLimitAnswers: formSelector(state, 'criteriaDailyLimitAnswers'),
      capInterval: formSelector(state, 'capInterval'),
      capAmount: formSelector(state, 'capAmount'),
      capSegmentConfiguration: formSelector(state, 'capSegmentConfiguration'),
      criteriaDayHourRestrictions: formSelector(state, 'criteriaDayHourRestrictions'),
      offerCriteriaId: formSelector(state, 'offerCriteriaId'),
    };
  });

  const isAnySelectorNotNullOrEmpty = Object.values(selectors).some(isValueNotNullOrEmpty);

  return {
    ...selectors,
    isAnySelectorNotNullOrEmpty,
  };
};

export const useAnalysisSelectors = () => {
  const selectors = useSelector((state) => {
    const formSelector = state?.form?.sequenceDetails ? sequenceDetailsSelector : getFormSelector();
    if (!formSelector) {
      console.warn('Form selector not found in state');
      return {};
    }

    return {
      randomFailureProbabilities: formSelector(state, 'randomFailureProbabilities'),
    };
  });

  const isAnySelectorNotNullOrEmpty = Object.values(selectors).some(isValueNotNullOrEmpty);

  return {
    ...selectors,
    isAnySelectorNotNullOrEmpty,
  };
};
